/* iter55g30 — Hiatus card for MyBillingPanel.
 *
 * Small billing-panel section that lets Pro+/Studio subscribers pause
 * their subscription for up to 90 days per calendar year, no minimum
 * block. Basic-tier users see an upgrade CTA instead of the picker.
 *
 * When paused: shows a countdown, an "End hiatus early" button that
 * credits unused days back to the yearly balance, and a subtle notice
 * that AI writes are paused during the break.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CalendarClock, Loader2, PauseCircle, PlayCircle, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

export default function HiatusCard() {
  const [state, setState] = useState(null);
  const [days,  setDays]  = useState(30);
  const [busy,  setBusy]  = useState(false);

  const reload = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/subscriptions/hiatus/status`,
                                { withCredentials: true });
      setState(r.data);
    } catch {
      setState(null);
    }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const start = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/subscriptions/hiatus/start`,
                       { days: Math.max(1, Math.min(90, Number(days) || 0)) },
                       { withCredentials: true });
      toast.success(`Hiatus started — ${days} days.`);
      reload();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d
        : (d?.message || 'Could not start hiatus.'));
    } finally { setBusy(false); }
  };

  const end = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/subscriptions/hiatus/end`, {},
                                  { withCredentials: true });
      toast.success(`Welcome back. ${r.data?.credited_days || 0} days credited.`);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not end hiatus.');
    } finally { setBusy(false); }
  };

  if (!state) return null;

  return (
    <div className="rounded-md border border-border bg-card"
         data-testid="billing-hiatus-card">
      <div className="flex items-center gap-2 border-b border-border/60 px-4 py-2">
        <CalendarClock className="h-3.5 w-3.5 text-primary" />
        <div className="flex-1 text-[12px] font-semibold">Take a break</div>
        <span className="font-mono-stat text-[10.5px] text-muted-foreground">
          {state.days_available_ytd}/{state.max_days_per_year} days available
        </span>
      </div>

      {!state.eligible && (
        <div className="flex items-center gap-2 p-4 text-[12px] text-muted-foreground"
             data-testid="billing-hiatus-locked">
          <Lock className="h-3.5 w-3.5 text-amber-500" />
          Available on Pro Author and Premier Studio plans.
        </div>
      )}

      {state.eligible && state.is_paused && (
        <div className="flex items-center justify-between gap-3 p-4"
             data-testid="billing-hiatus-active">
          <div className="text-[12px]">
            You&rsquo;re on a hiatus until{' '}
            <b>{new Date(state.paused_until).toLocaleDateString()}</b>.
            AI drafts and exports are paused; manuscripts and Bibles are safe.
          </div>
          <Button onClick={end} disabled={busy} size="sm"
                  className="h-9 gap-1.5 text-[12px]"
                  data-testid="billing-hiatus-end-btn">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <PlayCircle className="h-3.5 w-3.5" />}
            End early
          </Button>
        </div>
      )}

      {state.eligible && !state.is_paused && (
        <div className="flex flex-wrap items-center gap-3 p-4">
          <label className="flex items-center gap-2 text-[12px]">
            Pause for
            <Input
              type="number"
              min={1}
              max={state.days_available_ytd || 90}
              value={days}
              onChange={(e) => setDays(e.target.value)}
              className="h-8 w-20 font-mono-stat text-[12px]"
              data-testid="billing-hiatus-days-input"
            />
            days
          </label>
          <Button
            onClick={start}
            disabled={busy || state.days_available_ytd < 1}
            size="sm"
            className="h-9 gap-1.5 text-[12px]"
            data-testid="billing-hiatus-start-btn"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <PauseCircle className="h-3.5 w-3.5" />}
            Take a break
          </Button>
          <div className="text-[11px] text-muted-foreground">
            Any 90 days per year, no minimum block. Your manuscripts stay put.
          </div>
        </div>
      )}
    </div>
  );
}
